import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Table2, AlertTriangle, ClipboardCheck, ListChecks,
  FileBadge, GraduationCap, Eye, FileX2, ShieldCheck, Recycle, FileText,
  Settings as SettingsIcon, Shield, LogOut, Scale, AlertOctagon,
  BookOpen, FolderCog, Wrench, Siren, Users2, ClipboardSignature,
  BellRing, UserCog, ScrollText, Target,
} from "lucide-react";
import { useAuth } from "../../lib/auth";
import { useHSE } from "../../lib/store";
import { toast } from "sonner";

const GROUPS = [
  {
    label: "Core HSE",
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
      { to: "/monthly-kpi", label: "Monthly KPI Data", icon: Table2 },
      { to: "/incidents", label: "Incident Register", icon: AlertTriangle },
      { to: "/inspections", label: "Inspection Register", icon: ClipboardCheck },
      { to: "/action-plan", label: "Action Plan", icon: ListChecks },
      { to: "/permit-to-work", label: "Permit to Work", icon: FileBadge },
      { to: "/training", label: "Training Matrix", icon: GraduationCap },
      { to: "/observations", label: "Observations / SOR", icon: Eye },
    ],
  },
  {
    label: "ISO Governance",
    items: [
      { to: "/context-interested-parties", label: "Context & Parties", icon: Scale },
      { to: "/strategic-risks", label: "Strategic Risks", icon: AlertOctagon },
      { to: "/worker-consultation", label: "Worker Consultation", icon: Users2 },
      { to: "/management-of-change", label: "Management of Change", icon: FolderCog },
      { to: "/hse-objectives", label: "HSE Objectives", icon: Target, permission: "objectives.view" },
      { to: "/legal-compliance", label: "Legal Compliance", icon: Scale },
      { to: "/audit-ncr", label: "Audit Plans & NCRs", icon: ShieldCheck },
      { to: "/reports-center", label: "Governed Reports & Approvals", icon: FileText, permission: "reports.view" },
    ],
  },
  {
    label: "Audit & Compliance",
    items: [
      { to: "/ncr-register", label: "NCR Register", icon: FileX2 },
      { to: "/audits", label: "Audit Module", icon: ShieldCheck },
      { to: "/management-review", label: "Management Review", icon: ClipboardSignature, permission: "management_review.view" },
    ],
  },
  {
    label: "Health & Environment",
    items: [
      { to: "/industrial-hygiene", label: "Industrial Hygiene", icon: GraduationCap },
      { to: "/environmental-aspects", label: "Aspects & Impacts", icon: Recycle },
      { to: "/waste-manifests", label: "Waste Manifests", icon: Recycle },
      { to: "/calibration-assets", label: "Calibration Assets", icon: Wrench },
    ],
  },
  {
    label: "Registers",
    items: [
      { to: "/risk-assessment", label: "Risk Assessment", icon: AlertOctagon },
      { to: "/method-statement", label: "Method Statement", icon: BookOpen },
      { to: "/document-control", label: "Document Control", icon: FolderCog },
      { to: "/equipment-certification", label: "Equipment & Cert.", icon: Wrench },
      { to: "/emergency-preparedness", label: "Emergency / Drills", icon: Siren },
      { to: "/contractor-performance", label: "Contractor Perf.", icon: Users2 },
      { to: "/waste", label: "Waste / Environment", icon: Recycle },
    ],
  },
  {
    label: "Reports",
    items: [
      { to: "/reports", label: "Operational Reports", icon: FileText },
      { to: "/notifications", label: "Notifications & Alerts", icon: BellRing },
    ],
  },
  {
    label: "Admin",
    items: [
      { to: "/user-management", label: "User Management", icon: UserCog },
      { to: "/activity-log", label: "Activity Log", icon: ScrollText },
      { to: "/settings", label: "Settings", icon: SettingsIcon },
    ],
  },
];

export default function Sidebar() {
  const { logout, session, hasPermission } = useAuth();
  const { data } = useHSE();
  const nav = useNavigate();
  const clientName = session?.user?.organizationName || data.settings?.client || data.settings?.company || "Not configured";
  const companyName = data.settings?.company || "HSE Management System";

  const onLogout = async () => {
    if (!confirm("Sign out of the command center?")) return;
    await logout();
    toast.success("Signed out securely");
    nav("/login", { replace: true });
  };

  return (
    <aside
      data-testid="app-sidebar"
      className="hidden lg:flex flex-col w-[248px] shrink-0 h-screen sticky top-0"
      style={{ background: "#082B4D" }}
    >
      <div className="px-5 pt-5 pb-4 border-b border-white/10">
        <div className="flex items-center gap-2.5">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: "#18B84F" }}
          >
            <Shield className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div className="leading-tight">
            <div className="text-white font-heading font-bold text-[15px] tracking-tight">HSE Command</div>
            <div className="text-[10px] font-semibold tracking-[0.18em] uppercase text-[#18B84F]">
              ISO 45001 · Corporate
            </div>
          </div>
        </div>
        <div className="mt-4 rounded-lg bg-white/[0.06] border border-white/10 px-3 py-2.5">
          <div className="text-[9px] uppercase tracking-[0.18em] text-white/55 font-semibold">Client</div>
          <div className="text-white text-sm font-semibold mt-0.5 truncate">{clientName}</div>
          <div className="text-[10px] text-white/55 truncate">{companyName}</div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-3 px-2.5 sidebar-scroll" data-testid="sidebar-nav">
        {GROUPS.map((group) => (
          <div key={group.label} className="mb-3">
            <div className="px-2.5 pb-1.5 pt-2 text-[9px] uppercase tracking-[0.2em] text-white/45 font-semibold">
              {group.label}
            </div>
            {group.items.filter((n) => !n.permission || hasPermission(n.permission)).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`sidebar-${n.label.toLowerCase().replace(/[^\w]+/g, "-")}-link`}
                className={({ isActive }) =>
                  `group relative flex items-center gap-2.5 px-3 py-[7px] rounded-lg mb-0.5 transition-all duration-150 ${
                    isActive
                      ? "text-white shadow-sm"
                      : "text-white/70 hover:bg-white/[0.07] hover:text-white"
                  }`
                }
                style={({ isActive }) => (isActive ? { background: "#18B84F" } : {})}
              >
                <n.icon className="w-[16px] h-[16px] shrink-0" strokeWidth={1.75} />
                <span className="text-[12.5px] font-medium">{n.label}</span>
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <div className="px-2.5 pb-3 pt-2 border-t border-white/10">
        <button
          data-testid="sidebar-logout-btn"
          onClick={onLogout}
          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-white/70 hover:text-white hover:bg-rose-500/15 transition"
        >
          <LogOut className="w-[16px] h-[16px] shrink-0" strokeWidth={1.75} />
          <span className="text-[12.5px] font-medium">Sign out</span>
          {session?.user?.username && (
            <span className="ml-auto text-[10px] text-white/40 font-mono truncate max-w-[80px]">
              @{session.user.username}
            </span>
          )}
        </button>
      </div>

      <div className="px-4 py-3 border-t border-white/10 text-[10px] text-white/45 font-mono">
        v2.0 · {new Date().getFullYear()} © Command Center
      </div>
    </aside>
  );
}
